<template>
  <section id="contact" class="relative py-24 bg-slate-900 overflow-hidden">
    <!-- Decorative background elements -->
    <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500 z-10"></div>
    <div class="absolute -top-40 -right-40 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
    <div class="absolute -bottom-40 -left-40 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div class="bg-slate-800 rounded-3xl p-8 md:p-12 lg:p-16 shadow-2xl border border-slate-700">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16">
          
          <!-- Contact Info Side -->
          <div>
            <h2 class="text-amber-500 font-bold uppercase tracking-wider text-sm mb-2">Let's Connect</h2>
            <h3 class="text-3xl md:text-5xl font-extrabold text-white mb-6">We want to build trust and connection</h3>
            <p class="text-gray-400 text-lg mb-10">
              Ready to start your next project? Reach out to our team of experts to discuss how EM CONTRACTING CORP can bring your vision to life.
            </p>
            
            <div class="space-y-6">
              <div class="flex items-start">
                <div class="flex-shrink-0 mt-1">
                  <div class="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                    <svg class="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                  </div>
                </div>
                <div class="ml-4">
                  <h4 class="text-white font-medium text-lg">Headquarters</h4>
                  <p class="text-gray-400 mt-1" v-if="contactInfo.contact_address" v-html="contactInfo.contact_address.replace(/\n/g, '<br>')"></p>
                  <p class="text-gray-400 mt-1" v-else>156 E Milton St<br>Freeport, NY 11520, EE. UU.</p>
                </div>
              </div>

              <div class="flex items-start">
                <div class="flex-shrink-0 mt-1">
                  <div class="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                    <svg class="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                  </div>
                </div>
                <div class="ml-4">
                  <h4 class="text-white font-medium text-lg">Phone</h4>
                  <p class="text-gray-400 mt-1">{{ contactInfo.contact_phone || '+1 (516) 233-5434' }}</p>
                </div>
              </div>

              <div class="flex items-start">
                <div class="flex-shrink-0 mt-1">
                  <div class="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                    <svg class="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                  </div>
                </div>
                <div class="ml-4">
                  <h4 class="text-white font-medium text-lg">Email</h4>
                  <p class="text-gray-400 mt-1">{{ contactInfo.contact_email || 'estimate@emcontractingcorp.com' }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Form Side -->
          <div class="bg-slate-900 rounded-2xl p-8 border border-slate-700 shadow-inner">
            <form @submit.prevent="submitForm" class="space-y-6">
              <div>
                <label for="name" class="block text-sm font-medium text-gray-300">Full Name</label>
                <input type="text" id="name" v-model="form.name" required class="mt-2 block w-full bg-slate-800 border-slate-600 rounded-md shadow-sm text-white focus:ring-amber-500 focus:border-amber-500 py-3 px-4" placeholder="John Doe">
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label for="email" class="block text-sm font-medium text-gray-300">Email Address</label>
                  <input type="email" id="email" v-model="form.email" required class="mt-2 block w-full bg-slate-800 border-slate-600 rounded-md shadow-sm text-white focus:ring-amber-500 focus:border-amber-500 py-3 px-4" placeholder="john@example.com">
                </div>
                <div>
                  <label for="phone" class="block text-sm font-medium text-gray-300">Phone Number</label>
                  <input type="tel" id="phone" v-model="form.phone" required class="mt-2 block w-full bg-slate-800 border-slate-600 rounded-md shadow-sm text-white focus:ring-amber-500 focus:border-amber-500 py-3 px-4" placeholder="(555) 123-4567">
                </div>
              </div>

              <div>
                <label for="message" class="block text-sm font-medium text-gray-300">Project Details</label>
                <textarea id="message" v-model="form.message" required minlength="20" rows="4" class="mt-2 block w-full bg-slate-800 border-slate-600 rounded-md shadow-sm text-white focus:ring-amber-500 focus:border-amber-500 py-3 px-4" placeholder="Tell us about your project requirements..."></textarea>
              </div>

              <div>
                <button type="submit" :disabled="submitting" class="w-full flex justify-center py-4 border border-transparent rounded-md shadow-sm text-base font-bold text-slate-900 bg-amber-500 hover:bg-amber-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-amber-500 transition-colors disabled:opacity-50">
                  {{ submitting ? 'Sending...' : 'Send Message' }}
                </button>
              </div>
              
              <p v-if="successMsg" class="text-green-500 text-sm text-center font-medium">{{ successMsg }}</p>
              <p v-if="errorMsg" class="text-red-500 text-sm text-center font-medium">{{ errorMsg }}</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import apiClient from '@/services/api'

export default {
  name: 'ContactSection',
  data() {
    return {
      form: {
        name: '',
        email: '',
        phone: '',
        message: ''
      },
      submitting: false,
      successMsg: '',
      errorMsg: ''
    }
  },
  computed: {
    ...mapGetters('settings', ['contactInfo'])
  },
  methods: {
    async submitForm() {
      if (this.form.message.length < 20) {
        this.errorMsg = 'Please provide more details about your project (minimum 20 characters).'
        return
      }

      this.submitting = true
      this.successMsg = ''
      this.errorMsg = ''
      
      try {
        await apiClient.post('/quotes/construction', this.form)
        this.successMsg = 'Thank you! Your estimate request has been sent successfully.'
        this.form = { name: '', email: '', phone: '', message: '' }
      } catch (error) {
        this.errorMsg = 'Something went wrong. Please try again later.'
        console.error('Submission error:', error)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
